import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import { ENV_CONFIG } from '@ps/core-config';
import { LOADING_CONTEXT } from '@ps/core-overlay';
import { SurveyStatus } from '@ps/survey-kernel';

import type {
  ChildSurveyFilter,
  GetSurveysParams,
  Survey,
  SurveyMetadataForm,
  SurveyQuota,
  SurveyQuotaUpdate,
  SurveyStatusCount,
  SurveySupplierCpi,
  Targeting,
  UploadPsidFileResult,
} from '@ps/survey-utils';
import { User } from '@ps/auth-data-access';

import type { GetSurveyByIdDto } from './dtos/get-survey-by-id.dto';
import {
  SurveyCountResponseDto,
  SurveyDto,
  UpdateSurveyStatusPayloadDto,
  SurveyPatchPayloadDto,
  LaunchTrafficChannelRequestDto,
} from './dtos/survey.dto';
import { HighCpiConfirmationDetailsDto } from './dtos/high-cpi-confirmation.dto';
import { TcChannelDto } from './dtos/projects.dto';
import {
  SurveyQuotaDto,
  QuotaPatchOperationDto,
  QuotaUpdateResultDto,
} from './dtos/survey-quota.dto';
import { SurveyPayloadMapper } from './mapper/survey-payload.mapper';
import {
  BulkGroupSurveysPayloadMapper,
  BulkPatchCpiPayloadMapper,
} from './mapper/bulk-edit-payload.mapper';
import { SurveyMapper, TcChannelMapper } from './mapper/survey.mapper';
import { SurveySupplierCpiDto } from './dtos/survey-supplier-cpi.dto';
import { SurveySupplierCpiMapper } from './mapper/survey-supplier-cpi.mapper';
import { QuotaMapper } from './mapper/quota.mapper';
import { QuotaUpdateMapper } from './mapper/quota-update.mapper';
import {
  StandardSurveyPayloadMapper,
  StandardSurveyTargetingInput,
} from './mapper/standard-survey.payload.mapper';
import { BillingRuleRecord } from '@ps/user-settings-models';
import { UploadPsidFileResponseDto } from './dtos/upload-psid-file.dto';
import { UploadPsidFileMapper } from './mapper/upload-psid-file.mapper';
import { LoadingScope, withLoading } from '@ps/core-loading';

/** A single page of survey quotas plus the total count reported by the API. */
export interface QuotaPage {
  quotas: SurveyQuota[];
  total: number;
}

const CHILD_SURVEYS_PARAMS: Record<string, string | number> = {
  page: 1,
  status: 'all',
  targeting: 0,
};

@Injectable({ providedIn: 'root' })
export class SurveyApi {
  private readonly _env = inject(ENV_CONFIG);
  private readonly _http = inject(HttpClient);

  private readonly _baseUrl = `${this._env.pureSpectrum.url}`;
  private readonly _mapper = new SurveyMapper();
  private readonly _tcChannelMapper = new TcChannelMapper();
  private readonly _payloadMapper = new SurveyPayloadMapper();
  private readonly _quotaMapper = new QuotaMapper();
  private readonly _quotaUpdateMapper = new QuotaUpdateMapper();
  private readonly _uploadPsidFileMapper = new UploadPsidFileMapper();
  private readonly _bulkGroupSurveysPayloadMapper =
    new BulkGroupSurveysPayloadMapper();
  private readonly _supplierCpiMapper = new SurveySupplierCpiMapper();
  private readonly _cpiMapper = new BulkPatchCpiPayloadMapper();

  public getAll(data: GetSurveysParams): Observable<Survey[]> {
    const params = new HttpParams({
      fromObject: { ...this._payloadMapper.toPayload(data) },
    });
    return this._http
      .get<SurveyDto[]>(`${this._baseUrl}/buyers/v2/surveys?UI=1`, { params })
      .pipe(map((surveys) => this._mapper.fromDtoList(surveys)));
  }

  public getCounts(
    surveysParams: GetSurveysParams,
  ): Observable<SurveyStatusCount> {
    const params = new HttpParams({
      fromObject: { ...this._payloadMapper.toCountsPayload(surveysParams) },
    });
    return this._http
      .get<SurveyCountResponseDto>(
        `${this._baseUrl}/buyers/v2/surveys/count?UI=1`,
        { params },
      )
      .pipe(map((response) => this._mapper.fromCountResponseDto(response)));
  }

  public getChildSurveys(filter: ChildSurveyFilter): Observable<Survey[]> {
    const params = new HttpParams({
      fromObject: { ...CHILD_SURVEYS_PARAMS, ...filter },
    });
    return this._http
      .get<SurveyDto[]>(`${this._baseUrl}/buyers/v2/surveys?UI=1`, { params })
      .pipe(map((surveys) => this._mapper.fromDtoList(surveys)));
  }

  public getTrafficChannelChildren(surveyId: number): Observable<Survey[]> {
    return this._http
      .get<
        TcChannelDto[]
      >(`${this._baseUrl}/buyers/v2/surveys/${surveyId}/channels?UI=1`, { context: withLoading('drawer') })
      .pipe(map((dtos) => this._tcChannelMapper.fromDtoList(dtos)));
  }

  public updateSurveyStatus(
    surveyId: number,
    status: SurveyStatus,
    scope: LoadingScope,
  ): Observable<void> {
    const body: UpdateSurveyStatusPayloadDto = { ps_survey_status: status };
    return this._http.put<void>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/status?UI=1`,
      body,
      {
        context: withLoading(scope),
      },
    );
  }

  public getById(
    surveyId: number,
    targeting: Targeting = 0,
    accessible = false,
  ): Observable<Survey> {
    const params = new HttpParams({
      fromObject: {
        UI: 1,
        targeting,
        ...(accessible && { accessible }),
      },
    });
    return this._http
      .get<GetSurveyByIdDto>(`${this._baseUrl}/buyers/v2/surveys/${surveyId}`, {
        params,
        context: withLoading('drawer'),
      })
      .pipe(map((dto) => this._mapper.fromDetailsDto(dto)));
  }

  public getHighCpiDetails(
    surveyId: number,
  ): Observable<HighCpiConfirmationDetailsDto> {
    return this._http.get<HighCpiConfirmationDetailsDto>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/high-cpi-details`,
      {
        context: withLoading('drawer'),
      },
    );
  }

  public patchSurvey(
    surveyId: number,
    payload: SurveyPatchPayloadDto[],
    scope: LoadingScope,
  ): Observable<void> {
    return this._http.patch<void>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}`,
      payload,
      { context: withLoading(scope) },
    );
  }

  public updateCurrentCpi(
    surveyId: number,
    cpi: number,
    scope: LoadingScope = 'drawer',
  ): Observable<void> {
    return this.patchSurvey(
      surveyId,
      this._cpiMapper.toPayload({ updatedValue: cpi, error: null }),
      scope,
    );
  }

  public getSurveySupplierCpis(
    surveyId: number,
  ): Observable<SurveySupplierCpi[]> {
    const params = new HttpParams({
      fromObject: { mode: 'view', UI: 1 },
    });
    return this._http
      .get<
        SurveySupplierCpiDto[]
      >(`${this._baseUrl}/buyers/v2/surveys/${surveyId}/suppliers`, { params, context: withLoading('drawer') })
      .pipe(map((dtos) => this._supplierCpiMapper.fromDtoList(dtos)));
  }

  public getQuotas(
    surveyId: number,
    page?: number,
    limit?: number,
  ): Observable<QuotaPage> {
    const params = new HttpParams({
      fromObject: {
        UI: 1,
        QBS: 1,
        ...(page ? { page } : {}),
        ...(limit ? { limit } : {}),
      },
    });
    return this._http
      .get<
        SurveyQuotaDto[]
      >(`${this._baseUrl}/buyers/v2/surveys/${surveyId}/quotas`, { params, observe: 'response', context: withLoading('drawer') })
      .pipe(
        map((response) => ({
          quotas: this._quotaMapper.fromDtoList(response.body ?? []),
          total: Number(response.headers.get('total')) || 0,
        })),
      );
  }

  public setQuotaLock(
    surveyId: number,
    psQuotaId: string,
    locked: boolean,
  ): Observable<void> {
    const payload: QuotaPatchOperationDto[] = [
      { op: 'replace', path: '/lock', value: locked },
    ];
    return this._http.patch<void>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}/quotas/${psQuotaId}`,
      payload,
      {
        context: withLoading('drawer'),
      },
    );
  }

  public setQuotaCurrentTarget(
    surveyId: number,
    psQuotaId: string,
    currentTarget: number,
    updateSupplier: boolean,
  ): Observable<SurveyQuotaUpdate> {
    const payload: QuotaPatchOperationDto[] = [
      {
        op: 'replace',
        path: '/current_target',
        value: String(currentTarget),
        updateSupplier,
      },
    ];
    return this._http
      .patch<QuotaUpdateResultDto>(
        `${this._baseUrl}/buyers/v2/surveys/${surveyId}/quotas/${psQuotaId}`,
        payload,
        {
          context: withLoading('drawer'),
        },
      )
      .pipe(map((dto) => this._quotaUpdateMapper.fromDto(dto)));
  }

  public launchTrafficChannel(
    surveyId: number,
    payload: LaunchTrafficChannelRequestDto,
    bulk = false,
  ): Observable<Survey[]> {
    const params = new HttpParams({
      fromObject: { UI: '1', ...(bulk && { bulk: 'true' }) },
    });
    return this._http
      .post<
        TcChannelDto[]
      >(`${this._baseUrl}/buyers/v2/surveys/${surveyId}/channels`, payload, { params, context: withLoading('drawer') })
      .pipe(map((dtos) => this._tcChannelMapper.fromDtoList(dtos)));
  }

  public uploadPsidFile(
    surveyId: number,
    file: File,
  ): Observable<UploadPsidFileResult> {
    const formData = new FormData();
    formData.append('psid_ref_file', file);
    formData.append('surveyId', String(surveyId));
    return this._http
      .post<UploadPsidFileResponseDto>(
        `${this._baseUrl}/inclexcl/PSIDFile`,
        formData,
      )
      .pipe(map((dto) => this._uploadPsidFileMapper.fromDto(dto)));
  }

  public searchInvoicedSurveysWithBillingId(
    billingId: string,
  ): Observable<void> {
    const params = new HttpParams({
      fromObject: { billing: billingId },
    });
    return this._http.get<void>(
      `${this._baseUrl}/buyers/v2/surveys/invoiced/billing_id`,
      { params },
    );
  }

  public verifyBillingIdAgainstBillingRules(
    billingId: string,
    billingRuleRecord: BillingRuleRecord,
  ): Observable<void> {
    return this._http.post<void>(
      `${this._baseUrl}/buyers/v2/surveys/billig_id/rules/`,
      { po: billingId, billingRules: billingRuleRecord },
    );
  }

  public groupSurveys(
    surveyIds: number[],
    groupTitle: string,
  ): Observable<void> {
    const body = this._bulkGroupSurveysPayloadMapper.toPayload(
      surveyIds,
      groupTitle,
    );
    return this._http.post<void>(
      `${this._baseUrl}/buyers/v2/surveys/group`,
      body,
      { context: withLoading('drawer') },
    );
  }

  public deleteSurvey(surveyId: number): Observable<void> {
    return this._http.delete<void>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}`,
      { context: withLoading('drawer') },
    );
  }

  public createStandardSurvey(
    metadata: SurveyMetadataForm,
    user: User,
    targeting?: StandardSurveyTargetingInput,
  ): Observable<SurveyDto> {
    const payload = new StandardSurveyPayloadMapper().toPayload({
      metadata,
      companyId: user.companyId,
      userId: user.id,
      targeting,
    });
    return this._http.post<SurveyDto>(
      `${this._baseUrl}/buyers/v2/surveys?UI=1`,
      payload,
      {
        context: new HttpContext().set(LOADING_CONTEXT, 'page'),
      },
    );
  }

  // No `targeting` query param: it only shapes the response (defaulting to 1
  // returns the rehydrated quotas + qualifications), it never gates the write.
  public updateStandardSurvey(
    surveyId: number,
    metadata: SurveyMetadataForm,
    user: User,
    targeting?: StandardSurveyTargetingInput,
  ): Observable<SurveyDto> {
    const payload = new StandardSurveyPayloadMapper().toPayload({
      metadata,
      companyId: user.companyId,
      userId: user.id,
      targeting,
    });
    return this._http.put<SurveyDto>(
      `${this._baseUrl}/buyers/v2/surveys/${surveyId}?UI=1`,
      payload,
      {
        context: new HttpContext().set(LOADING_CONTEXT, 'page'),
      },
    );
  }
}
